import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import useSettings from '../hooks/useSettings';

const PrivacyDisclaimer = ({ onAccept, show: externalShow, onClose }) => {
  // If externalShow is provided, use it; otherwise default to internal state
  const [internalShow, setInternalShow] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Determine if modal should be shown based on props or internal state
  const show = externalShow !== undefined ? externalShow : internalShow;
  
  // Use the settings hook to get privacy policy content
  const { getSetting, isLoading: settingsLoading, isLoaded } = useSettings();
  
  // Update privacy policy content when settings are loaded
  useEffect(() => {
    // Always start with loading state true
    setLoading(true);
    
    if (isLoaded) {
      // Only update content when settings are fully loaded
      const privacyContent = getSetting('privacy_disclosure', '');
      
      if (privacyContent) {
        setPrivacyPolicy(privacyContent);
      } else {
        // Fallback content if no privacy policy is set and settings are loaded
        setPrivacyPolicy('<p>This application uses AI technology to provide chat-based assistance. Your conversations may be processed and stored to improve our services.</p><p>By clicking "Accept", you agree to our privacy policy and terms of service.</p>');
      }
      
      // Only set loading to false when settings are fully loaded
      setLoading(false);
    }
  }, [isLoaded, getSetting]); // Depend on isLoaded and getSetting
  
  // Check if user has already accepted the disclaimer
  useEffect(() => {
    // Only run the auto-show logic if externalShow is not provided
    if (externalShow === undefined) {
      
        setInternalShow(true);
      
    }
  }, [onAccept, externalShow]);

  const handleAccept = () => {    
    // Use internal state if no external control
    if (externalShow === undefined) {
      setInternalShow(false);
    }
    
    // Call onClose if provided
    if (onClose) {
      onClose();
    }
    
    // Call onAccept if provided
    if (onAccept) {
      onAccept();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose || handleAccept}
      backdrop="static" 
      keyboard={false}
      centered
      className="privacy-modal"
    >
      <Modal.Header>
        <Modal.Title>Privacy Disclaimer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center p-4">
            <div className="d-flex flex-column align-items-center justify-content-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="progress mb-2" style={{ width: '80%', height: '8px' }}>
                <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-3 privacy-modal-body" dangerouslySetInnerHTML={{ __html: privacyPolicy }}></div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="primary" 
          onClick={handleAccept} 
          disabled={loading}
          className="privacy-accept-btn"
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Loading...
            </>
          ) : 'Accept'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrivacyDisclaimer;
